import { IconNames } from '@blueprintjs/icons';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { Button } from '@strata/react-buttons';
import { CollapsibleSection } from '@strata/react-collapsible-section';
import { DateInputFormik } from '@strata/react-date-time';
import { TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Icon } from '@strata/react-status-icons';
import { getIn, useFormikContext } from 'formik';
import PropTypes from 'prop-types';

import { checkBenefitForMember } from 'api/scopeAssessorAPI';
import { handleError } from 'services/Error';
import { Sections } from 'services/priorAuthorizationHelpers';
import { useGetPayerSegmentsQuery } from 'store/api/referenceApi';

const getId = (fieldId) => `${Sections.PATIENT}.${fieldId}`;

export const PatientSection = ({ setOpenSection, setShowPatientDialog, setLoadingText }) => {
    const formik = useFormikContext();

    const { data: segments, isFetching: segmentsFetching } = useGetPayerSegmentsQuery();
    const { payerSegmentList } = segments ?? {};

    const { eligibility: _eligibility, ...patientErrors } =
        getIn(formik.errors, Sections.PATIENT) ?? {};
    const sectionTouched = getIn(formik.touched, Sections.PATIENT);

    const checkPatientBenefit = async ({
        healthPlan,
        patientFirstName: firstName,
        patientLastName: lastName,
        memberDOB: dateOfBirth,
        cardholderId,
    }) => {
        try {
            const benefitCheck = await checkBenefitForMember({
                payerId: healthPlan?.payerId,
                segmentId: healthPlan?.secondaryData,
                firstName,
                lastName,
                dateOfBirth,
                cardholderId,
            });
            await formik.setFieldValue(getId('eligibility'), benefitCheck);
            setShowPatientDialog(true);
        } catch (err) {
            handleError("Can't retrieve patient benefit check results.", err);
        }
    };

    const resetEligibility = async () => {
        if (getIn(formik.values, getId('eligibility'))) {
            await formik.setFieldValue(getId('eligibility.eligibilityConfirmed'), false);
        }
    };

    return (
        <>
            <SuggestSingleSelectFormik
                id={getId('healthPlan')}
                label="Health Plan"
                placeholderText="Select Patient Plan"
                values={payerSegmentList}
                isLoading={segmentsFetching}
                onChange={async (newValue) => {
                    await formik.setFieldValue(getId('healthPlan'), newValue);
                    await resetEligibility();
                }}
                required
            />
            <TextInputFormik
                id={getId('patientFirstName')}
                label="Patient First Name"
                placeholderText="Enter Patient First Name"
                onChange={async (e) => {
                    await formik.setFieldValue(getId('patientFirstName'), e.target.value);
                    await resetEligibility();
                }}
                required
            />
            <TextInputFormik
                id={getId('patientLastName')}
                label="Patient Last Name"
                placeholderText="Enter Patient Last Name"
                onChange={async (e) => {
                    await formik.setFieldValue(getId('patientLastName'), e.target.value);
                    await resetEligibility();
                }}
                required
            />
            <TextInputFormik
                id={getId('cardholderId')}
                label="Cardholder ID"
                placeholderText="Enter Cardholder ID"
                onChange={async (e) => {
                    await formik.setFieldValue(getId('cardholderId'), e.target.value);
                    await resetEligibility();
                }}
                required
            />
            <DateInputFormik
                id={getId('memberDOB')}
                required
                hideCalendarPopover
                label="Member Date of Birth"
                onChange={async (newValue) => {
                    await formik.setFieldValue(
                        getId('memberDOB'),
                        formatDate(newValue, DateFormats.HYPHEN_DATE),
                    );
                    await resetEligibility();
                }}
            />
            <div
                style={{ display: 'flex', alignItems: 'center' }}
                className="padding__bottom--x-large"
            >
                <Button
                    intent={Button.Intent.PRIMARY}
                    disabled={Object.keys(patientErrors).length > 0 || !sectionTouched}
                    label="Check Patient Benefits"
                    onClick={async () => {
                        setLoadingText('Checking Patient Benefits');
                        await checkPatientBenefit(getIn(formik.values, Sections.PATIENT));
                        setLoadingText();
                    }}
                />
                {getIn(formik.values, getId('eligibility.eligibilityConfirmed')) && (
                    <span
                        style={{ display: 'flex', alignItems: 'center' }}
                        className="padding__left--medium"
                    >
                        <Icon
                            icon={IconNames.TICK_CIRCLE}
                            intent={Icon.Intent.SUCCESS}
                            size={14}
                            className="padding__right--x-small"
                        />
                        Member eligibility confirmed
                    </span>
                )}
            </div>
            <div className="padding__bottom--medium">
                <CollapsibleSection
                    title="Additional Patient Detail"
                    renderExpanded={false}
                    content={
                        <TextInputFormik
                            id={getId('memberBenefitCode')}
                            label="Member Benefit Code"
                        />
                    }
                />
            </div>
            <div style={{ display: 'flex', justifyContent: 'right' }}>
                <Button
                    intent={Button.Intent.PRIMARY}
                    disabled={
                        Object.keys(getIn(formik.errors, Sections.PATIENT) ?? {}).length > 0 ||
                        !sectionTouched
                    }
                    label="Continue"
                    onClick={() => setOpenSection((state) => state + 1)}
                />
            </div>
        </>
    );
};

PatientSection.propTypes = {
    setOpenSection: PropTypes.func.isRequired,
    setShowPatientDialog: PropTypes.func.isRequired,
    setLoadingText: PropTypes.func.isRequired,
};
